import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  getEventMessages,
} from '../../redux/actions/events';
import { getEventBookings } from '../../redux/actions/bookings';
import { getEffectiveBookingStatus } from '../../utils/bookingActionability';

import BookingStatus, {
  CancelBookingButton,
  PayBookingButton,
} from 'components/BookingStatus';
import BookingManagement from 'components/BookingManagement';
import EventEditForm from 'components/EventEditForm';
import SpacesOverviewPanel from 'components/SpacesOverviewPanel';

import {
  CloseBtn,
  EventHeaderCard,
  TabBar,
} from './parts/atoms';
import UpdatesTab from './parts/UpdatesTab';
import InviteTab from './parts/InviteTab';
import { EMAIL_INVITES_ENABLED } from 'utils/features';

// Tabs are addressed by key (deep links pass `emTab`), so adding one never
// shifts the others.
const PROMOTER_TAB_KEYS = [
  'bookings',
  'places',
  'updates',
  ...(EMAIL_INVITES_ENABLED ? ['invite'] : []),
  'edit',
];
const BRAND_TAB_KEYS = ['updates', 'my-booking'];

const resolveInitialKey = (isPromoter, initialTabKey) => {
  const key = (initialTabKey || '').toLowerCase();
  if (isPromoter) {
    if (key === 'manage-bookings' || key === 'manage' || key === 'bookings') return 'bookings';
    if (key === 'places-overview' || key === 'booking-status' || key === 'places') return 'places';
    if (key === 'updates') return 'updates';
    if (EMAIL_INVITES_ENABLED && (key === 'invite' || key === 'invites')) {
      return 'invite';
    }
    if (key === 'edit' || key === 'edit-event') return 'edit';
    return 'bookings';
  }
  if (
    key === 'booking-status' ||
    key === 'status' ||
    key === 'view-status' ||
    key === 'my-booking'
  ) {
    return 'my-booking';
  }
  return 'updates';
};

const EventManagerModal = ({ isOpen, onClose, event, user, initialTabKey }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isPromoter = user?.role === 'promoter';
  const eventId = event?.id;

  const [tabKey, setTabKey] = useState(
    resolveInitialKey(isPromoter, initialTabKey)
  );
  const [bookingsForOverview, setBookingsForOverview] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setTabKey(resolveInitialKey(isPromoter, initialTabKey));
  }, [isOpen, isPromoter, initialTabKey]);

  // Bookings slice (for KPI counts + Places overview)
  const allBookings = useSelector((s) => s.bookings?.bookings);
  const eventBookings = useMemo(() => {
    const list = allBookings || [];
    return list.filter((b) => Number(b?.eventId) === Number(eventId));
  }, [allBookings, eventId]);

  // Fetch event messages (mirrors prior logic — brand only after approved/paid)
  useEffect(() => {
    if (!isOpen || !eventId) return;
    if (isPromoter) {
      dispatch(getEventMessages(eventId));
      return;
    }
    const bStatus = event?.booking?.status;
    if (bStatus === 'Approved' || bStatus === 'Paid') {
      dispatch(getEventMessages(eventId));
    }
  }, [isOpen, eventId, isPromoter, event?.booking?.status, dispatch]);

  // Fetch bookings for KPI strip + Places overview (promoter only)
  useEffect(() => {
    if (!isOpen || !eventId || !isPromoter) return;
    (async () => {
      try {
        const data = await dispatch(getEventBookings(eventId));
        if (Array.isArray(data)) setBookingsForOverview(data);
      } catch (e) {
        // Silently ignore — header KPIs degrade gracefully
      }
    })();
  }, [isOpen, eventId, isPromoter, dispatch]);

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Effective status, so the badge matches the Requests bucket in
  // BookingManagement — a Pending booking past its expireAt is no longer a
  // request the promoter can act on.
  const pendingCount = useMemo(
    () =>
      eventBookings.filter((b) => getEffectiveBookingStatus(b) === 'pending')
        .length,
    [eventBookings]
  );

  const tabs = useMemo(() => {
    if (isPromoter) {
      return [
        {
          key: 'bookings',
          label: t('dashboard.em.tab_bookings'),
          count: pendingCount || undefined,
        },
        { key: 'places', label: t('dashboard.em.tab_places') },
        { key: 'updates', label: t('dashboard.em.tab_updates') },
        ...(EMAIL_INVITES_ENABLED
          ? [{ key: 'invite', label: t('dashboard.em.tab_invite') }]
          : []),
        { key: 'edit', label: t('dashboard.em.tab_edit') },
      ];
    }
    return [
      { key: 'updates', label: t('dashboard.em.tab_updates') },
      { key: 'my-booking', label: t('dashboard.em.tab_my_booking') },
    ];
  }, [isPromoter, t, pendingCount]);

  const handlePreview = () => {
    if (event?.slug) {
      navigate(`/event/${event.slug}`);
      onClose?.();
    }
  };

  if (!isOpen || !event) return null;

  const tabKeys = isPromoter ? PROMOTER_TAB_KEYS : BRAND_TAB_KEYS;
  const tabIndex = Math.max(0, tabKeys.indexOf(tabKey));
  const setTabIndex = (i) => setTabKey(tabKeys[i]);

  const panel = (() => {
    if (isPromoter) {
      if (tabKey === 'bookings') {
        return (
          <BookingManagement
            eventId={event?.id}
            eventSlug={event?.slug}
            event={event}
            // Sharing lives on the My Events cards; with email invites off
            // the button keeps its old behaviour (open the event page).
            onInvite={
              EMAIL_INVITES_ENABLED ? () => setTabKey('invite') : undefined
            }
          />
        );
      }
      if (tabKey === 'places') {
        return (
          <SpacesOverviewPanel
            event={event}
            spaces={event?.spaces || event?.Spaces || []}
            bookings={bookingsForOverview}
          />
        );
      }
      if (tabKey === 'updates') {
        return <UpdatesTab event={event} user={user} isPromoter />;
      }
      if (tabKey === 'invite') {
        return <InviteTab event={event} />;
      }
      // Cancelling leaves this modal holding a stale event, so close it.
      if (tabKey === 'edit')
        return (
          <EventEditForm
            event={event}
            onCancelled={onClose}
            onDeleted={onClose}
          />
        );
    } else {
      if (tabKey === 'updates') {
        return <UpdatesTab event={event} user={user} isPromoter={false} />;
      }
      if (tabKey === 'my-booking') return <BookingStatus event={event} user={user} />;
    }
    return null;
  })();

  return createPortal(
    <Dialog open={isOpen} onClose={onClose} className="relative z-[1001]">
      <DialogBackdrop
        transition
        className="fixed inset-0 transition ease-out duration-200
                   data-[leave]:ease-in data-[leave]:duration-150 data-[closed]:opacity-0"
        style={{
          background:
            'radial-gradient(1200px 700px at 30% 0%, #fff8e8 0%, #ececf0 55%)',
        }}
      />

      <div
        className="fixed inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.08) 100%)',
        }}
      />

      {/* Bottom sheet below lg (like BookingBubble's mobile panel), centered card on desktop */}
      <div className="fixed inset-0 flex items-end lg:items-center justify-center lg:p-8">
        <DialogPanel
          transition
          className="relative w-full bg-white ring-1 ring-black/5 overflow-hidden flex flex-col
                     rounded-t-[22px] rounded-b-none lg:rounded-[22px]
                     transition ease-out duration-300 data-[leave]:ease-in data-[leave]:duration-200
                     data-[closed]:translate-y-full
                     lg:duration-200 lg:data-[leave]:duration-150
                     lg:data-[closed]:translate-y-0 lg:data-[closed]:opacity-0 lg:data-[closed]:scale-95"
          style={{
            maxWidth: 1100,
            height: 'min(90vh, 860px)',
            boxShadow:
              '0 30px 80px -20px rgba(0,0,0,0.25), 0 8px 24px -8px rgba(0,0,0,0.15)',
          }}
        >
          <CloseBtn onClick={onClose} label={t('common.close', 'Close')} />

          <div className="shrink-0 px-5 pt-5 pb-0 sm:px-6 sm:pt-6 [@media(max-height:700px)]:px-4 [@media(max-height:700px)]:pt-4">
            <EventHeaderCard
              event={event}
              isPromoter={isPromoter}
              onPreview={handlePreview}
            />
          </div>

          <div className="flex-1 min-h-0 flex flex-col px-5 pb-5 pt-3 sm:px-6 sm:pb-6 [@media(max-height:700px)]:px-4 [@media(max-height:700px)]:pb-4 [@media(max-height:700px)]:pt-2.5">
            <div className="shrink-0 mb-3 flex items-center justify-between gap-3 [@media(max-height:700px)]:mb-2">
              <TabBar
                tabs={tabs}
                activeIndex={tabIndex}
                onChange={setTabIndex}
              />
              {!isPromoter && tabKey === 'my-booking' && (
                <div className="flex items-center gap-2 shrink-0">
                  <PayBookingButton event={event} />
                  <CancelBookingButton event={event} />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden em-nice-scroll pr-1 pt-1 pl-1 -ml-1">
              {panel}
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>,
    document.body
  );
};

export default EventManagerModal;
